import { DateTime } from 'luxon';
import { z } from 'zod';
import { apiEnvelope } from '../envelope';

/**
 * Absolute upper bound on the per-program entries one check-in may carry. This
 * is a payload-size safety ceiling ONLY — deliberately NOT the program count.
 *
 * The client parses this schema before transport, so any bound tied to the
 * program catalog would freeze the allowed program count into the shipped app
 * bundle: when the catalog later grew, older builds would reject legitimate
 * larger home check-ins on-device — the client-release-per-program coupling
 * ADR-015 exists to avoid (and the exact class of bug this ticket fixed at a
 * lower value). So the ceiling is set well above any realistic number of
 * concurrent member programs; the catalog can grow freely underneath it with no
 * client release, while a runaway/oversized array is still rejected.
 */
export const MAX_CHECK_IN_ENTRIES = 32;

// Wire schemas for the check-ins resource group (ADR-002 envelope).
//
// A check-in is a stream write: the transport is condition-agnostic and keyed
// to member programs. Identity comes from the JWT, never the body — no
// `userId`/`clerkUserId`/`memberId` field is declared here (anti-IDOR,
// ADR-028/015). Wire keys are camelCase (ADR-066). Unknown keys are stripped,
// not honored.

/**
 * True iff `s` is a real `YYYY-MM-DD` calendar date — shape AND a valid
 * month/day. The shape regex alone accepts impossible dates (`2026-99-99`,
 * `2026-02-30`) that become an `Invalid Date` downstream; the round-trip check
 * rejects them so callers get a deterministic 400 / fallback, not a runtime failure.
 */
export function isCalendarDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** Calendar date in the member's local day, `YYYY-MM-DD`. */
export const checkInDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD')
  .refine(isCalendarDate, 'date must be a real calendar date');

// Client input affordances for the member's own free text (a custom tag they
// type, the note they write). These cap what the member ENTERS on-device; they
// are NOT transport-validation bounds on the wire shape — see the structural
// `checkInContextSchema` note below. The server owns the payload-abuse ceiling
// (see `apps/api` check-in request schema).
export const CHECK_IN_TAG_MAX_LENGTH = 60;
export const CHECK_IN_NOTES_MAX_LENGTH = 1000;

/**
 * The tag-group key mood selections are submitted under. The check-in summary
 * step collects mood as tags in this server-served group (not the legacy
 * `moodBefore`/`moodAfter` fields), so readers that surface "how the member
 * felt" resolve this group first and treat the fields as a legacy fallback.
 */
export const CHECK_IN_MOOD_GROUP_KEY = 'mood';

/**
 * Tag groups keyed by group name → selected tags (e.g. `{ mood: ['good'] }`).
 * Structural only: any non-empty group key mapping to non-empty tag strings.
 * It deliberately does NOT bound group count, tags-per-group, or tag length —
 * those values echo the server-served vocabulary, and AC-7 / ADR-015 require
 * that growing the catalog (more groups, more tags, a longer label) needs zero
 * client change. Payload-abuse ceilings are enforced server-side, where they
 * never gate a member's device on a client release.
 */
export const checkInContextSchema = z.record(z.string().min(1), z.array(z.string().min(1)));

/** One per-program entry. `memberProgramId` is validated for ownership server-side. */
export const checkInEntryInputSchema = z
  .object({
    memberProgramId: z.string().uuid(),
    quantity: z.number().int().nonnegative(),
    isFree: z.boolean(),
  })
  .strict();
export type CheckInEntryInput = z.infer<typeof checkInEntryInputSchema>;

/**
 * `{ quantity: 0, isFree: false }` is a contradiction, not a record.
 *
 * The two fields answer different questions — how much was logged, and whether
 * the member declared the day free — and this combination answers them
 * incompatibly: nothing was logged, and the day was not free. No surface can
 * render it truthfully. `buildCheckinSummaryElements` gives it neither the count
 * card (the total is not above zero) nor the free-day card (nothing declared it
 * free), so the day renders as an informationally empty section.
 *
 * Both honest encodings already exist and are what every writer should produce:
 * `{ quantity: 0, isFree: true }` for a day the member declared free, and
 * `{ quantity: 1, isFree: false }` for "used, count not recorded".
 */
export function isContradictoryCheckInEntry(entry: { quantity: number; isFree: boolean }): boolean {
  return entry.quantity === 0 && entry.isFree === false;
}

/**
 * Whether a detected contradiction should fail the parse.
 *
 * Fatal under test and dev, silent in release: a shape this rare should stop a
 * developer immediately, and should not cost a member their save in production,
 * where refusing the write would turn a display gap into data loss.
 *
 * The release branch emits NOTHING, deliberately. A raw `console.error` here
 * would be an unstructured write from production code carrying none of the
 * required trace context (ADR-004 Rules 3 and 10), and routing the provider
 * into this module would make every check-in write on every client pay for an
 * alarm that has no live trigger — no shipped write path can produce the shape.
 * If a signal is ever wanted, it belongs at a boundary that already holds the
 * provider, not in a schema.
 *
 * So this is a DEV-AND-CI CONTRACT, not a runtime guard — and that is stronger
 * than "release is lenient" makes it sound. Two facts, both checkable:
 *
 *   - No deployed Lambda sets `NODE_ENV` at all (`infra/app/lib` contains no
 *     reference to it), so the API resolves the lenient branch in every
 *     deployed environment, not merely in prod.
 *   - `process.env?.NODE_ENV` read through optional chaining is not what a
 *     bundler's `define` substitution matches, so the Atlas browser bundle
 *     resolves it lenient too.
 *
 * It therefore binds exactly one population: the next engineer's tests and CI.
 * That is worth having — a write path added later fails at commit time rather
 * than shipping — but it is not a runtime rejection, and a reader should not
 * plan around it as one. Where the invariant should ultimately be enforced is
 * an open question with its own ticket.
 *
 * `NODE_ENV` is checked FIRST and the check does not rest on `__DEV__`.
 * `apps/mobile/test/setup.ts` pins `__DEV__` to `false`, so a `__DEV__`-only
 * predicate is inert under vitest — silently green in the one environment whose
 * whole job is to catch this.
 */
function contradictionIsFatal(): boolean {
  const nodeEnv = typeof process === 'undefined' ? undefined : process.env?.NODE_ENV;
  if (nodeEnv === 'test' || nodeEnv === 'development') return true;
  // `Reflect.get` rather than an assertion on `globalThis`: `__DEV__` is a
  // React Native global this package cannot declare (ADR-117 — no type
  // escapes), and it is how the mobile test setup writes the value too.
  return Reflect.get(globalThis, '__DEV__') === true;
}

/**
 * A validated check-in always has a primary entry. Model that invariant in the
 * inferred type as well as at runtime so downstream services never have to
 * assert that `entries[0]` exists.
 *
 * This is also where the contradiction guard lives, and the placement is
 * deliberate on both axes.
 *
 * WHY HERE rather than on `checkInEntryInputSchema`: a refinement on that object
 * would return a wrapped schema with no `.shape`, and two existing tests
 * introspect exactly that (`packages/prompts` reads the entry's field names,
 * `reward-config.contract` reads the create request's). Nothing parses a single
 * entry on its own — every real write validates an `entries` array — so guarding
 * the array covers every runtime path while leaving the introspectable object
 * intact.
 *
 * WHY HERE rather than at each call site: every write-side request schema in the
 * repo derives from this one — the mobile create and update bodies, their
 * server-bounded counterparts used by both the public v1 and the internal
 * care-team handlers, the batch endpoint, and preview seeding. Asserting the
 * property once means a write path added later inherits it without doing
 * anything, which a pair of call-site checks could never promise.
 *
 * NOT on the read schema. `checkInEntrySchema` must keep accepting the shape:
 * the Tier-0 migration writes it for real legacy records that carried a zero
 * count without an abstinence flag, and rejecting those on read would make
 * migrated history unreadable — turning a cosmetic gap into data loss. The
 * migration is the one writer this guard does not reach, by design; whether the
 * invariant should also hold below the request layer is a separate decision.
 */
export const checkInEntriesInputSchema = z
  .tuple([checkInEntryInputSchema], checkInEntryInputSchema)
  .refine((entries) => entries.length <= MAX_CHECK_IN_ENTRIES, {
    message: `entries must contain at most ${MAX_CHECK_IN_ENTRIES} items`,
  })
  .superRefine((entries, ctx) => {
    entries.forEach((entry, index) => {
      if (!isContradictoryCheckInEntry(entry)) return;
      if (!contradictionIsFatal()) return;
      ctx.addIssue({
        code: 'custom',
        path: [index],
        message:
          'entry has quantity 0 with isFree false — a day cannot log nothing and also not be free. Use { quantity: 0, isFree: true } for a declared free day, or { quantity: 1, isFree: false } for use with no recorded count.',
      });
    });
  })
  .meta({
    // The tuple carries non-empty-array inference for TypeScript. Its public
    // wire shape remains a homogeneous bounded array, so override only the
    // generated JSON Schema representation from the canonical item schema.
    type: 'array',
    items: z.toJSONSchema(checkInEntryInputSchema, { target: 'openapi-3.0' }),
    minItems: 1,
    maxItems: MAX_CHECK_IN_ENTRIES,
  });

/**
 * `POST /api/v1/check-ins` body. Carries one entry per program the member is
 * checking in for, bounded by `MAX_CHECK_IN_ENTRIES` (the enrollable-program
 * catalog size) so a member enrolled in the full program set can submit a
 * complete day in one write. The member is the JWT; no identity field is
 * accepted here.
 */
export const checkInCreateRequestSchema = z.object({
  date: checkInDateSchema,
  context: checkInContextSchema.optional(),
  notes: z.string().max(CHECK_IN_NOTES_MAX_LENGTH).optional(),
  // mood values are server-served vocabulary (like tags) — structural only,
  // no length coupling (AC-7). Nullable so a replace can explicitly CLEAR a
  // previously-recorded mood: absent ⇒ preserve (server merge), `null` ⇒ clear,
  // non-empty string ⇒ set.
  moodBefore: z.string().min(1).nullable().optional(),
  moodAfter: z.string().min(1).nullable().optional(),
  entries: checkInEntriesInputSchema,
});
export type CheckInCreateRequest = z.infer<typeof checkInCreateRequestSchema>;

/**
 * `PUT /api/v1/check-ins/{date}` body — the create body minus `date` (the date
 * is the path param). Replacing a day's check-in is a correcting-append: the
 * server re-writes a superseding `checked-in` event for that date, reusing the
 * create write path (reads take latest-per-date, so the new event wins).
 */
export const checkInUpdateRequestSchema = checkInCreateRequestSchema.omit({ date: true });
export type CheckInUpdateRequest = z.infer<typeof checkInUpdateRequestSchema>;

/** A reconstructed per-program entry as returned to clients. */
export const checkInEntrySchema = z.object({
  memberProgramId: z.string(),
  programKey: z.string(),
  quantity: z.number().int().nonnegative(),
  isFree: z.boolean(),
  unit: z.string().nullable(),
});
export type CheckInEntry = z.infer<typeof checkInEntrySchema>;

/**
 * One tag group as a reader renders it: the opaque group key, and the resolved
 * display strings stored against it.
 *
 * The single declaration of this shape. `orderedContextGroups` and
 * `latestContextGroups` in `./recency` both return it, `checkInDataSchema`
 * below carries it on the wire, and the home summary's `todayContextGroups`
 * ALIASES it rather than re-declaring an identical object
 * (`../home/schema.ts`) — the same reasoning as
 * `memberCustomTagResolvedSchema` further down this file: sharing the object
 * makes the two incapable of diverging instead of merely expected not to. A
 * field added here reaches every reader, which is the point.
 *
 * Declared here rather than in `./recency` because the dependency runs
 * recency → schema; putting it there and importing it back would be a cycle.
 */
export const checkInContextGroupSummarySchema = z.object({
  group: z.string(),
  tags: z.array(z.string()),
  /**
   * The group's card heading on the Home "Tracked" section, resolved
   * server-side from `checkin.contextGroup_<group>_summaryLabel` wherever the
   * responding Lambda ships the catalog. Absent on the mood group, and absent
   * on every group from a catalog-free responder (`/home/summary`), where the
   * client resolves the same key itself.
   */
  label: z.string().optional(),
});
export type CheckInContextGroupSummary = z.infer<typeof checkInContextGroupSummarySchema>;

/** A reconstructed check-in (round-trips a posted one). */
export const checkInDataSchema = z.object({
  date: checkInDateSchema,
  context: checkInContextSchema.nullable(),
  notes: z.string().nullable(),
  moodBefore: z.string().nullable(),
  moodAfter: z.string().nullable(),
  entries: z.array(checkInEntrySchema),
  /**
   * The moods this day records, resolved through the ONE precedence definition
   * (`latestMoods`): the served `mood` tag group when it carries anything, else
   * the legacy `moodAfter`/`moodBefore` scalar. Derived, never stored and never
   * accepted on a write — `checkInCreateRequestSchema` does not declare it, so
   * a client that sends one has it stripped rather than honoured.
   *
   * Here so a reader asking for an arbitrary date gets the same answer the home
   * summary's `todayMoods` gives for today, without the client having to learn
   * the precedence (ADR-015).
   *
   * Optional for the additive-compat reason the home summary's own fields carry:
   * a shipped app bundle must keep parsing a response from an API deployed
   * before this field existed, and an older bundle strips it from a newer
   * response rather than rejecting the body. A deployed server always sends it
   * (possibly empty).
   */
  moods: z.array(z.string()).optional(),
  /**
   * Every non-empty tag group this day records, in the CATALOG'S SERVED group
   * order, with any group the payload carries that the catalog no longer serves
   * appended after them — `orderedContextGroups`, the same function and the
   * same argument the home summary uses for `todayContextGroups`.
   *
   * `mood` is NOT excluded: this is the faithful projection of what was stored.
   * The one consumer that must drop it — the "surrounding" card — does so in the
   * shared surface author, where every caller inherits the rule.
   *
   * Distinct from `context` beside it, which is the raw stored record in
   * whatever key order the client wrote it. Both are carried because they answer
   * different questions: `context` is what the member recorded, this is how the
   * catalog presents it. Derived, never stored, never accepted on a write.
   *
   * Optional for the same additive-compat reason as `moods`.
   */
  contextGroups: z.array(checkInContextGroupSummarySchema).optional(),
  /**
   * Present (true) only on days written through the batch backfill surface
   * (e.g. the intake baseline import). Distinguishes imported history from
   * interactive check-ins where the difference matters (the weekly tracking
   * challenge); latest-wins means a later interactive write on the same date
   * sheds the marker.
   */
  backfill: z.boolean().optional(),
});
export type CheckInData = z.infer<typeof checkInDataSchema>;

export const checkInResponseSchema = apiEnvelope(checkInDataSchema);
export type CheckInResponse = z.infer<typeof checkInResponseSchema>;

/**
 * What a WRITE returns: the reconstructed check-in plus the reward points that
 * write actually persisted, summed across the member's programs.
 *
 * Deliberately a separate shape rather than an optional field on
 * `checkInDataSchema`. A check-in reconstructed from the event stream carries
 * no earn — the ledger rows are not replayed on read — and the batch backfill
 * surface mints nothing by design. Extending the shared shape would let both
 * of those paths report an earn they never computed, so only the two write
 * routes speak this schema (ADR-066: one owner per type dimension).
 *
 * `0` is a real, meaningful value: a same-day repeat check-in is dropped by the
 * ledger's unique `source_ref`, and a member whose programs run no rewards loop
 * earns nothing. Clients render that case as "no points earned", never as a
 * missing value.
 */
export const checkInWriteDataSchema = checkInDataSchema.extend({
  pointsEarned: z.number().int().nonnegative(),
});
export type CheckInWriteData = z.infer<typeof checkInWriteDataSchema>;

export const checkInWriteResponseSchema = apiEnvelope(checkInWriteDataSchema);
export type CheckInWriteResponse = z.infer<typeof checkInWriteResponseSchema>;

/**
 * What a CLIENT parses a write response with. Identical to the producer shape
 * except `pointsEarned` is optional, because the two sides deploy
 * independently: a shipped app build must keep working against an API deployed
 * before the field existed.
 *
 * The split is deliberate and the strictness runs opposite ways on purpose. The
 * producer schema (above) keeps the field REQUIRED so `sendOk` rejects a server
 * that forgot to compute it — that is the contract. If the client parsed with
 * that same schema, an older API would fail envelope validation and a
 * successfully-persisted check-in would surface to the member as a failed
 * write. Clients treat an absent value as "no points reported" and render the
 * no-earn surface; they never invent a number.
 */
export const checkInWriteClientDataSchema = checkInDataSchema.extend({
  pointsEarned: z.number().int().nonnegative().optional(),
});
export type CheckInWriteClientData = z.infer<typeof checkInWriteClientDataSchema>;

export const checkInListResponseSchema = apiEnvelope(z.array(checkInDataSchema));
export type CheckInListResponse = z.infer<typeof checkInListResponseSchema>;

/** `POST /api/v1/check-ins/batch` body — 1–31 entries, one per calendar day. */
export const checkInBatchRequestSchema = z.object({
  items: z.array(checkInCreateRequestSchema).min(1).max(31),
});
export type CheckInBatchRequest = z.infer<typeof checkInBatchRequestSchema>;

export const checkInBatchResponseSchema = apiEnvelope(z.array(checkInDataSchema));
export type CheckInBatchResponse = z.infer<typeof checkInBatchResponseSchema>;

/**
 * One of the member's own tags. Unlike a built-in tag — which is a bare display
 * string resolved from the locale catalog — a custom tag carries an `id`,
 * because the member can rename and delete it. `label` is member-authored
 * content served verbatim: it has no slug and deliberately no locale key
 * (ADR-017 governs product copy, not what a member typed).
 *
 * NOT `.strict()`, for the same reason the enclosing group schema is not. This
 * object is nested inside that deliberately-open group, so leaving it strict
 * would have reintroduced exactly the failure the relaxation exists to prevent,
 * one level down: a field the server later adds INSIDE a `customTags` entry
 * would hard-fail the parse on every already-installed bundle, even though the
 * group around it tolerates additions. An open shape at the top and a closed one
 * immediately inside it is not additive tolerance — it is a trap.
 */
export const memberCustomTagSchema = z.object({
  id: z.string().uuid(),
  groupKey: z.string().min(1),
  label: z.string().min(1),
});
export type MemberCustomTag = z.infer<typeof memberCustomTagSchema>;

/**
 * One selectable tag group in the check-in context catalog. `key` is the
 * opaque group identifier the client echoes back as a `context` record key;
 * `label`, `tags`, and `customTags` are display vocabulary. The whole
 * vocabulary is server-owned (ADR-015) — the client renders whatever groups
 * arrive.
 *
 * `tags` holds the built-in labels ONLY — bare strings, no ids, never mixed
 * with custom entries. The member's own tags ride alongside in `customTags`
 * rather than being appended: the client needs an id to rename or delete one,
 * and needs to know a tag is custom to count it as `custom` in analytics
 * without emitting the label. Widening `tags` into an object array would have
 * been the same information at the cost of a breaking change to the field the
 * tag chip row renders.
 *
 * NOT `.strict()`, deliberately. ADR-015 makes the vocabulary server-owned and
 * the client a renderer of whatever arrives; a strict parse contradicts that by
 * turning any future additive server field into a client-side hard failure on
 * every already-installed bundle. `customTags` itself is the first field that
 * would have hit it.
 */
export const checkInContextGroupSchema = z.object({
  key: z
    .string()
    .min(1)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'key must be kebab-case'),
  label: z.string().min(1),
  tags: z.array(z.string().min(1)),
  // Defaulted so a response predating this field still parses.
  customTags: z.array(memberCustomTagSchema).default([]),
});
export type CheckInContextGroup = z.infer<typeof checkInContextGroupSchema>;

/** Member-scoped tag vocabulary for the check-in summary step. */
export const checkInContextCatalogSchema = z.object({
  groups: z.array(checkInContextGroupSchema),
});
export type CheckInContextCatalog = z.infer<typeof checkInContextCatalogSchema>;

export const checkInContextCatalogResponseSchema = apiEnvelope(checkInContextCatalogSchema);
export type CheckInContextCatalogResponse = z.infer<typeof checkInContextCatalogResponseSchema>;

/**
 * Per-member ceilings on the durable vocabulary. Deliberately generous — far
 * above plausible real use — because they bound abuse, they do not shape
 * behaviour. Soft-deleted rows count toward neither.
 */
export const MEMBER_CUSTOM_TAG_PER_GROUP_CEILING = 50;
export const MEMBER_CUSTOM_TAG_TOTAL_CEILING = 200;

/**
 * A member-authored label as accepted on the wire: trimmed, non-empty, and
 * within the same bound the tag input enforces on-device. Trimming happens
 * during parse (not after) so every downstream comparison — the collision
 * lookup, the uniqueness index, the stored value — sees the same string.
 */
export const memberCustomTagLabelSchema = z
  .string()
  .transform((value) => value.trim())
  .pipe(z.string().min(1, 'label must not be blank').max(CHECK_IN_TAG_MAX_LENGTH));

/** `POST /api/v1/check-ins/custom-tags` body. */
export const memberCustomTagCreateRequestSchema = z
  .object({
    // Bounded at 64 to match the `groupKey` schema registered in the analytics
    // forwarder's allowlist. The value is rejected against the live group
    // registry before anything is written, so an over-long key never reaches
    // storage — but it does reach an error message, and an unbounded field on a
    // public route is worth closing regardless.
    groupKey: z.string().min(1).max(64),
    label: memberCustomTagLabelSchema,
  })
  .strict();
export type MemberCustomTagCreateRequest = z.infer<typeof memberCustomTagCreateRequestSchema>;

/** `PATCH /api/v1/check-ins/custom-tags/{id}` body. */
export const memberCustomTagRenameRequestSchema = z
  .object({ label: memberCustomTagLabelSchema })
  .strict();
export type MemberCustomTagRenameRequest = z.infer<typeof memberCustomTagRenameRequestSchema>;

/**
 * How a create or rename resolved. The discriminator exists because the three
 * outcomes are indistinguishable from the status code alone, and the client
 * must not render a second chip for a write that created nothing:
 *
 * - `created`  — a row was written; `tag.id` is present.
 * - `builtin`  — the label matched a built-in in that group; NO row was written
 *                and `tag.id` is absent, because there is no row to address.
 * - `existing` — the label matched one of the member's live tags; no row was
 *                written and `tag` is that row, with its original casing.
 */
export const MEMBER_CUSTOM_TAG_RESOLUTION = ['created', 'builtin', 'existing'] as const;
export const memberCustomTagResolutionSchema = z.enum(MEMBER_CUSTOM_TAG_RESOLUTION);
export type MemberCustomTagResolution = z.infer<typeof memberCustomTagResolutionSchema>;

/**
 * The resolved tag when a row backs it — `created` and `existing` both name a
 * real row, so the id is required.
 *
 * Aliased to `memberCustomTagSchema` rather than re-declared. The two were
 * byte-identical, but only `memberCustomTagSchema` is pinned by the ADR-066
 * drift guard, so a field added to the list/catalog shape would silently leave
 * the write-response arms behind — precisely the drift that guard exists to
 * catch. Sharing the object makes them incapable of diverging instead of
 * merely expected not to.
 */
export const memberCustomTagResolvedSchema = memberCustomTagSchema;

/**
 * The resolved tag for a `builtin` resolution: a catalog entry, which has no row
 * and therefore no id. `id` is not merely optional here — it is absent, because
 * there is nothing for it to point at.
 */
export const memberCustomTagBuiltInSchema = z.object({
  groupKey: z.string().min(1),
  label: z.string().min(1),
});

/**
 * A discriminated union on `resolution`, not a flat object with an optional id.
 *
 * The flat shape let `{ resolution: 'created', tag: { …no id } }` and
 * `{ resolution: 'builtin', tag: { id } }` both validate, contradicting the very
 * contract its comment described — and no positive-path test can catch a shape
 * the schema accepts. Discriminating makes the id's presence a property the
 * parser enforces, and makes a client that reads `tag.id` after checking
 * `resolution !== 'builtin'` type-safe without a non-null assertion.
 */
export const memberCustomTagWriteSchema = z.discriminatedUnion('resolution', [
  z.object({ resolution: z.literal('created'), tag: memberCustomTagResolvedSchema }),
  z.object({ resolution: z.literal('existing'), tag: memberCustomTagResolvedSchema }),
  z.object({ resolution: z.literal('builtin'), tag: memberCustomTagBuiltInSchema }),
]);
export type MemberCustomTagWrite = z.infer<typeof memberCustomTagWriteSchema>;

export const memberCustomTagWriteResponseSchema = apiEnvelope(memberCustomTagWriteSchema);
export type MemberCustomTagWriteResponse = z.infer<typeof memberCustomTagWriteResponseSchema>;

export const memberCustomTagListSchema = z
  .object({ tags: z.array(memberCustomTagSchema) })
  .strict();
export type MemberCustomTagList = z.infer<typeof memberCustomTagListSchema>;

export const memberCustomTagListResponseSchema = apiEnvelope(memberCustomTagListSchema);
export type MemberCustomTagListResponse = z.infer<typeof memberCustomTagListResponseSchema>;

/**
 * Soft-delete acknowledgement. A literal rather than a "did this call change
 * anything" boolean, deliberately: for a tag the member owns, the route reports
 * the END STATE — the tag is not in their vocabulary — so a first delete and a
 * repeat delete answer identically. Reporting whether *this* call did the work
 * would turn an idempotent retry into a distinguishable outcome.
 *
 * Ownership is a separate question, answered before this shape is reached: an id
 * that is not the member's, or does not exist at all, is a 404 rather than a
 * `deleted: true`. That asymmetry is deliberate too — 200 here would confirm to
 * a caller that someone else's id is real.
 */
export const memberCustomTagDeleteSchema = z.object({ deleted: z.literal(true) }).strict();
export type MemberCustomTagDelete = z.infer<typeof memberCustomTagDeleteSchema>;

export const memberCustomTagDeleteResponseSchema = apiEnvelope(memberCustomTagDeleteSchema);
export type MemberCustomTagDeleteResponse = z.infer<typeof memberCustomTagDeleteResponseSchema>;

// ─── Voice-agent read surface ─────────────────────────────────────────────────
//
// The voice agent reads check-in history over the server-tool surface (ADR-045).
// The projection strips `memberProgramId` — the agent needs the condition-agnostic
// program key and the member's values, never an internal ownership identifier.
// Anchoring both date-string endpoints to UTC midnight keeps the inclusive day
// count exact integer arithmetic with no DST edge (ADR-096).

/** Maximum inclusive day window the voice agent may query in one call. */
export const MAX_CHECK_IN_QUERY_DAYS = 31;

function utcDayStart(date: string): DateTime {
  return DateTime.fromISO(date, { zone: 'utc' });
}

function inclusiveDayCount(from: string, to: string): number {
  return utcDayStart(to).diff(utcDayStart(from), 'days').days + 1;
}

/** Agent-facing projection of a check-in entry — `memberProgramId` omitted. */
export const agentCheckInEntrySchema = checkInEntrySchema.omit({ memberProgramId: true }).strict();
export type AgentCheckInEntry = z.infer<typeof agentCheckInEntrySchema>;

/** Minimized check-in record returned to the voice agent. */
export const agentCheckInSchema = z
  .object({
    date: checkInDateSchema,
    context: checkInContextSchema.nullable(),
    notes: z.string().nullable(),
    moodBefore: z.string().nullable(),
    moodAfter: z.string().nullable(),
    entries: z.array(agentCheckInEntrySchema),
  })
  .strict();
export type AgentCheckIn = z.infer<typeof agentCheckInSchema>;

/**
 * Inclusive date-range parameters for the `get_check_ins` voice tool —
 * at most `MAX_CHECK_IN_QUERY_DAYS` calendar days.
 */
export const getCheckInsParamsSchema = z
  .object({
    from: checkInDateSchema,
    to: checkInDateSchema,
  })
  .strict()
  .superRefine(({ from, to }, ctx) => {
    const days = inclusiveDayCount(from, to);
    if (days < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'to must be on or after from',
      });
    } else if (days > MAX_CHECK_IN_QUERY_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: `date range must not exceed ${MAX_CHECK_IN_QUERY_DAYS} days`,
      });
    }
  });
export type GetCheckInsParams = z.infer<typeof getCheckInsParamsSchema>;

/** Response payload the `get_check_ins` server tool returns to ElevenLabs. */
export const getCheckInsResultSchema = z
  .object({
    from: checkInDateSchema,
    to: checkInDateSchema,
    count: z.number().int().nonnegative(),
    checkIns: z.array(agentCheckInSchema),
  })
  .strict()
  .superRefine(({ from, to, count, checkIns }, ctx) => {
    if (count !== checkIns.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['count'],
        message: 'count must equal checkIns length',
      });
    }
    checkIns.forEach((checkIn, index) => {
      if (checkIn.date < from || checkIn.date > to) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['checkIns', index, 'date'],
          message: 'check-in date must be inside the requested range',
        });
      }
    });
  });
export type GetCheckInsResult = z.infer<typeof getCheckInsResultSchema>;
